import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, Store } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useStore } from "@/context/StoreProvider";
import ImageUpload from "@/components/common/ImageUpload";
import ConfirmActionDialog from "@/components/common/ConfirmActionDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const inputCls =
  "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-brand";
const AVAIL = ["in_stock", "out_of_stock", "preorder"];

function empty() {
  return { name: "", sku: "", price: 0, category: "", brand: "", description: "", images: [], availability: "in_stock" };
}

export default function Products() {
  const t = useT();
  const { store } = useStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    if (!store) { setLoading(false); return; }
    try {
      const { data } = await api.get("/products", { params: { store_id: store.id } });
      setProducts(data);
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, sku: p.sku || "", price: p.price || 0, category: p.category || "", brand: p.brand || "", description: p.description || "", images: p.images || [], availability: p.availability || "in_stock" });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success(t("products.updated"));
      } else {
        await api.post("/products", payload, { params: { store_id: store.id } });
        toast.success(t("products.created"));
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await api.delete(`/products/${confirmDel.id}`);
    toast.success(t("products.deleted"));
    load();
  };

  if (!store) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <Store className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">{t("dashboard.home.noStore")}</p>
        <Link to="/dashboard/store" className="mt-4 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">{t("dashboard.home.noStoreCta")}</Link>
      </div>
    );
  }

  const availLabel = (a) => ({ in_stock: t("products.inStock"), out_of_stock: t("products.outOfStock"), preorder: t("products.preorder") }[a]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">{t("products.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("products.subtitle")}</p>
        </div>
        <button onClick={openNew} data-testid="add-product-btn"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95">
          <Plus className="h-4 w-4" />{t("products.add")}
        </button>
      </div>

      {loading ? (
        <div className="mt-10 h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />
      ) : products.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-border py-16 text-center" data-testid="products-empty">
          <Package className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">{t("products.empty")}</p>
        </div>
      ) : (
        <div className="mt-8 overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm" data-testid="products-table">
            <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 font-medium">{t("products.name")}</th>
                <th className="hidden p-3 font-medium sm:table-cell">{t("products.sku")}</th>
                <th className="hidden p-3 font-medium md:table-cell">{t("products.category")}</th>
                <th className="p-3 font-medium">{t("products.price")}</th>
                <th className="hidden p-3 font-medium lg:table-cell">{t("products.aiScore")}</th>
                <th className="p-3 text-right font-medium">{t("products.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 transition-colors hover:bg-secondary/30" data-testid={`product-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-border bg-secondary">
                        {p.images?.[0] ? <img src={p.images[0]} alt="" className="h-full w-full object-cover" /> : <Package className="m-2 h-5 w-5 text-muted-foreground" />}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-medium">{p.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{p.brand}</p>
                      </div>
                    </div>
                  </td>
                  <td className="hidden p-3 font-mono text-xs text-muted-foreground sm:table-cell">{p.sku || "—"}</td>
                  <td className="hidden p-3 md:table-cell">{p.category || "—"}</td>
                  <td className="p-3 font-mono">{store.currency} {Number(p.price).toFixed(2)}</td>
                  <td className="hidden p-3 lg:table-cell">
                    {p.ai ? <span className="font-mono font-bold text-brand">{p.ai.ai_score}</span> : <span className="text-xs text-muted-foreground">{t("products.notScored")}</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setConfirmDel(p)} data-testid={`delete-product-${p.id}`} className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? t("products.editProduct") : t("products.newProduct")}</DialogTitle>
            <DialogDescription>{t("products.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("products.name")}</label>
              <input data-testid="product-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("products.namePlaceholder")} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("products.sku")}</label>
                <input data-testid="product-sku" className={inputCls} value={form.sku} onChange={(e) => set("sku", e.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("products.price")}</label>
                <input data-testid="product-price" type="number" step="0.01" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("products.category")}</label>
                <input data-testid="product-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("products.brand")}</label>
                <input data-testid="product-brand" className={inputCls} value={form.brand} onChange={(e) => set("brand", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("products.availability")}</label>
              <select data-testid="product-availability" className={inputCls} value={form.availability} onChange={(e) => set("availability", e.target.value)}>
                {AVAIL.map((a) => <option key={a} value={a}>{availLabel(a)}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("products.description")}</label>
              <textarea data-testid="product-description" className={`${inputCls} h-24 py-2`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("products.descriptionPlaceholder")} />
            </div>
            <ImageUpload label={t("products.images")} multiple value={form.images} onChange={(v) => set("images", v)} testid="product-images-upload" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary">{t("common.cancel")}</button>
            <button onClick={save} disabled={saving || !form.name.trim()} data-testid="product-save"
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95 disabled:opacity-60">
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmActionDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title={t("products.deleteTitle")}
        description={t("products.deleteDesc")}
        resourceName={confirmDel?.name}
        onConfirm={remove}
      />
    </div>
  );
}
